import Tab from "@Components/Tabs/Tab";
import Tabs from "@Components/Tabs/Tabs";
import TabsList from "@Components/Tabs/TabsList";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { useRef, memo, useCallback, useEffect } from "react";
import MapV3 from "../MapV3";
import styles from "./mapInfoTabs.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { useMapState, useMapDispatch } from "../../context/MapContext";
import MapModalV3 from "../MapModalV3";
import { PAGE } from "constants/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import {
  BusStopTab,
  PharmaciesTab,
  RestaurantsTab,
  SuperMarketTab,
  TrainStationTab,
  UniversitiesTab,
  HospitalsTab,
  PoliceTab,
  ShoppingMallsTab,
} from "@Icons/MapTabIcons";
import ChevronRightIcon from "@Icons/ChevronRightIcon";
import { showInKmsOrMiles } from "../../utils/utils";
//

type MapInfoTabsProps = {
  isDesktop: boolean;
  inventory: any;
  insideModal?: boolean;
  isDesktopStreetView?: boolean;
  handleOptionClick?: (item: any) => void;
};

const MapInfoTabs = ({
  isDesktop,
  inventory,
  insideModal = false,
  isDesktopStreetView = false,
  handleOptionClick,
}: MapInfoTabsProps) => {
  const { t } = useTranslationHook();
  const scrollHeightRef = useRef<HTMLDivElement | null>(null);

  // Use separate context hooks for state and dispatch
  const state = useMapState();
  const dispatch = useMapDispatch();

  // Destructure only the state properties we need
  const {
    tabs,
    activeTab,
    selectedOption,
    isLoading,
    error,
    isMapScriptLoaded,
    showModal,
    showMiles,
    universities,
  } = state;

  const scrollHeight = scrollHeightRef.current?.offsetTop ?? 600;
  const mobileItems = insideModal ? activeTab?.items : activeTab?.items?.slice(0, 5);
  const activeTabItems = isDesktop ? activeTab?.items : mobileItems;

  const updateTabState = useCallback(
    (tab: any) => {
      dispatch({ type: "SET_ACTIVE_TAB", payload: tab });
    },
    [dispatch],
  );

  // Auto-scroll the active tab into view within its container (horizontal only)
  useEffect(() => {
    if (activeTab?.name) {
      const tabEl = document.getElementById(`property-map-tab-${activeTab.name}`);
      if (tabEl && tabEl.parentElement) {
        const container = tabEl.parentElement;
        const scrollLeft = tabEl.offsetLeft - container.offsetWidth / 2 + tabEl.offsetWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    }
  }, [activeTab?.name]);

  const handleModalOpen = useCallback(() => {
    dispatch({ type: "SET_MAP_INITIALIZED", payload: false });
    dispatch({ type: "SET_MAP_BUILT", payload: false });
    dispatch({ type: "SHOW_MODAL", payload: true });
    gtagEventEmitter({
      action: "map_mobile_view_modal_opened",
      category: PAGE.INVENTORY,
      label: "",
    });
  }, [dispatch]);

  useEffect(() => {
    const onOpenMapWhenClicked = () => {
      handleModalOpen();
    };
    window.addEventListener("open_map_when_clicked", onOpenMapWhenClicked);
    return () => {
      window.removeEventListener("open_map_when_clicked", onOpenMapWhenClicked);
    };
  }, [handleModalOpen]);

  const handleModalClose = () => {
    dispatch({ type: "SET_MAP_INITIALIZED", payload: false });
    dispatch({ type: "SET_MAP_BUILT", payload: false });
    dispatch({ type: "SHOW_MODAL", payload: false });
    dispatch({ type: "SET_SHOW_OPTIONS", payload: false });
    dispatch({ type: "SET_OPTIONS", payload: universities });
  };
  // Note: The standalone popstate listener that was here has been removed.
  // MapModalV3 already uses useModal (via isHistoryPush={!isDesktop}) which
  // pushes a history entry and listens for popstate to close the modal.
  // Having a second popstate listener here caused a double back-navigation
  // on Android: the close button triggered goBack(), which fired both
  // useModal's popstate handler AND this one, resulting in the user being
  // navigated away from the page after the modal closed.

  const TAB_ICON_MAP: any = {
    Universities: <UniversitiesTab isSelected={activeTab?.name === "Universities"} />,
    "Bus Stations": <BusStopTab isSelected={activeTab?.name === "Bus Stations"} />,
    "Train Stations": <TrainStationTab isSelected={activeTab?.name === "Train Stations"} />,
    Supermarkets: <SuperMarketTab isSelected={activeTab?.name === "Supermarkets"} />,
    Pharmacies: <PharmaciesTab isSelected={activeTab?.name === "Pharmacies"} />,
    Restaurants: <RestaurantsTab isSelected={activeTab?.name === "Restaurants"} />,
    Hospitals: <HospitalsTab isSelected={activeTab?.name === "Hospitals"} />,
    Police: <PoliceTab isSelected={activeTab?.name === "Police"} />,
    "Shopping Malls": <ShoppingMallsTab isSelected={activeTab?.name === "Shopping Malls"} />,
  };

  // Render places list items
  const renderPlacesListItems = () => {
    if (isLoading) return <div className={styles.loading}>Loading...</div>;
    if (error) return <div className={styles.error}>{error}</div>;

    return (
      <>
        {activeTabItems?.map((item: any, idx: number) => (
          <div
            className={formatClassNames(
              styles.placesListItem,
              item?.name === selectedOption?.name && styles.placesListItem_selected,
            )}
            key={`${item?.name}-${idx}`}
            onClick={() => {
              dispatch({ type: "SET_VALUE", payload: "" });
              handleOptionClick?.(item);
            }}
            role="none"
          >
            <span
              data-testid="PlaceListItem-Recommendations"
              className={formatClassNames(styles.placesListItem_name, isDesktop && styles.desktop)}
            >
              {item.name}
            </span>
            <span
              data-testid="PlaceListItem-Recommendations-Distance"
              className={styles.placesListItem_distance}
            >
              {item.distance ? showInKmsOrMiles(item.distance, showMiles, t) : ""}
            </span>
          </div>
        ))}
      </>
    );
  };

  // Render desktop view
  const renderDesktopView = () => (
    <div style={{ position: "relative" }}>
      <div className={styles.miles}>
        <div className={styles.mapTypeButtonGroup}>
          <div
            data-testid="property-Map-View"
            role="none"
            className={showMiles ? styles.mapTypeButtonActive : styles.mapTypeButton}
            onClick={() => dispatch({ type: "SHOW_MILES", payload: true })}
          >
            {t("propertypage.components.mapsectionv2.miles")}
          </div>
          <div
            data-testid="property-Street-View"
            role="none"
            className={!showMiles ? styles.mapTypeButtonActive : styles.mapTypeButton}
            onClick={() => dispatch({ type: "SHOW_MILES", payload: false })}
          >
            {t("propertypage.components.mapsectionv2.kilometers")}
          </div>
        </div>
      </div>
      <Tabs defaultValue={activeTab} activeTab={activeTab} setActiveTab={updateTabState}>
        <TabsList className={formatClassNames(styles.tabList, styles.desktop)}>
          {tabs.map((tab: any) => (
            <Tab
              key={tab.name}
              id={`property-map-tab-${tab.name}`}
              dataTestId={`property-map-${tab.name}`}
              value={tab}
              className={styles.tab}
              withoutBorder
            >
              {TAB_ICON_MAP[tab.name]}
              {t(tab.name)}
            </Tab>
          ))}
        </TabsList>

        <div className={formatClassNames(styles.tabPanel, styles.desktop)}>
          <div className={formatClassNames(styles.tabPanelContainer, styles.desktop)}>
            <div
              className={formatClassNames(
                styles.mapDesktop,
                isDesktopStreetView && styles.streetView,
              )}
            >
              <MapV3
                inventory={inventory}
                isDesktop={isDesktop}
                onOptionClick={handleOptionClick}
                isMapLoaded={isMapScriptLoaded}
              />
            </div>

            <div
              className={formatClassNames(styles.placesListDesktopContainer)}
              ref={scrollHeightRef}
            >
              {renderPlacesListItems()}
            </div>
          </div>
        </div>
      </Tabs>
    </div>
  );

  // Render mobile view
  const renderMobileView = () => (
    <>
      <div className={styles.containerMobileMaps}>
        <MapV3
          inventory={inventory}
          isDesktop={isDesktop}
          onOptionClick={handleOptionClick}
          isMapLoaded={isMapScriptLoaded}
          onClick={handleModalOpen}
        />
        <div className={formatClassNames(styles.mapTypeButtonGroup, !isDesktop && styles.isMobile)}>
          <div
            data-testid="property-Map-View"
            role="none"
            className={showMiles ? styles.mapTypeButtonActive : styles.mapTypeButton}
            onClick={() => dispatch({ type: "SHOW_MILES", payload: true })}
          >
            {t("propertypage.components.mapsectionv2.miles")}
          </div>
          <div
            data-testid="property-Street-View"
            role="none"
            className={!showMiles ? styles.mapTypeButtonActive : styles.mapTypeButton}
            onClick={() => dispatch({ type: "SHOW_MILES", payload: false })}
          >
            {t("propertypage.components.mapsectionv2.kilometers")}
          </div>
        </div>
        <Tabs defaultValue={activeTab} activeTab={activeTab} setActiveTab={updateTabState}>
          <TabsList className={styles.tabList}>
            {tabs.map((tab: any) => (
              <Tab
                key={tab.name}
                id={`property-map-tab-${tab.name}`}
                dataTestId={`property-map-${tab.name}`}
                value={tab}
                className={styles.tab}
                withoutBorder
              >
                {TAB_ICON_MAP[tab.name]}
                {t(tab.name)}
              </Tab>
            ))}
          </TabsList>

          <div
            className={styles.tabPanel}
            style={
              insideModal
                ? { height: `calc(100vh - ${scrollHeight + 80}px)`, overflow: "scroll" }
                : {}
            }
          >
            <div className={styles.tabPanelContainer}>
              <div ref={scrollHeightRef}>
                {renderPlacesListItems()}
                {!isDesktop && (
                  <div
                    className={styles.placesListFooter}
                    onClick={handleModalOpen}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleModalOpen();
                      }
                    }}
                  >
                    {t("propertypage.components.mapsectionv2.viewmorenearbyplaces")}
                    <ChevronRightIcon />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Tabs>
      </div>
      {showModal && renderModalView()}
    </>
  );
  const renderModalView = () => (
    <>
      <MapModalV3
        title={t("propertypage.components.mapsectionv2.explorelocationtitle")}
        onClose={handleModalClose}
      >
        <div className={styles.mapContainerModal}>
          <MapV3
            inventory={inventory}
            isDesktop={false}
            onOptionClick={handleOptionClick}
            isMapLoaded={isMapScriptLoaded}
            insideModal
            showModal={showModal}
          />
        </div>
        <div style={{ marginTop: "80px" }}>
          <Tabs defaultValue={activeTab} activeTab={activeTab} setActiveTab={updateTabState}>
            <TabsList className={styles.tabList}>
              {tabs.map((tab: any) => (
                <Tab
                  key={tab.name}
                  id={`property-map-tab-${tab.name}`}
                  dataTestId={`property-map-${tab.name}`}
                  value={tab}
                  className={styles.tab}
                  withoutBorder
                >
                  {TAB_ICON_MAP[tab.name]}
                  {t(tab.name)}
                </Tab>
              ))}
            </TabsList>

            <div
              className={styles.tabPanel}
              style={
                insideModal
                  ? { height: `calc(100vh - ${scrollHeight + 80}px)`, overflow: "scroll" }
                  : {}
              }
            >
              <div className={styles.tabPanelContainer}>
                <div ref={scrollHeightRef}>{renderPlacesListItems()}</div>
              </div>
            </div>
          </Tabs>
        </div>
      </MapModalV3>
    </>
  );

  return isDesktop ? renderDesktopView() : renderMobileView();
};

// Wrap the component with memo to prevent unnecessary re-renders
const MemoizedMapInfoTabs = memo(MapInfoTabs);

export default wrapperHOC(MemoizedMapInfoTabs, {
  componentName: "MapsInfo-Tabs",
  showForChina: true,
});
